import { useState } from 'react';
import { Sparkles, Info, ChevronDown, ChevronUp, RefreshCw, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';

/**
 * Document 2 · Phase 6.3.2 — Detection Badge
 *
 * Two display modes:
 *   - readOnly={true}  → compact pill ("Detected: solo · events") with info popover
 *                        listing the 3 strongest hit signals + matched events keywords
 *   - readOnly={false} → adds a collapsible "Adjust detection" override row with
 *                        two Selects (scale, mode) and a Regenerate button
 *
 * Override flow: when the rep changes a select and clicks "Regenerate with
 * override", we call the parent's onRegenerate(overrides) handler. The parent
 * is responsible for re-POSTing /vision-proposal with the override fields and
 * updating the proposal state.
 */

const SCALE_OPTIONS = [
  { value: 'solo', label: 'Solo / Micro' },
  { value: 'mid', label: 'Mid-market' },
  { value: 'enterprise', label: 'Enterprise' },
];

const MODE_OPTIONS = [
  { value: 'events', label: 'Events / Seasonal' },
  { value: 'standard', label: 'Standard B2B' },
];

function _topHitSignals(signals, limit = 3) {
  if (!Array.isArray(signals)) return [];
  return signals.filter((s) => s?.hit).slice(0, limit);
}

function _formatKeywords(arr, limit = 5) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr.slice(0, limit);
}

export default function DetectionBadge({
  proposal,
  readOnly = true,
  onRegenerate,
  regenerating = false,
}) {
  const [showOverride, setShowOverride] = useState(false);
  const businessScale = proposal?.business_scale || proposal?.detected_business_scale || 'unknown';
  const detectionMode = proposal?.detection_mode || proposal?.detected_detection_mode || 'unknown';
  const detectedScale = proposal?.detected_business_scale || businessScale;
  const detectedMode = proposal?.detected_detection_mode || detectionMode;
  const overridden = !!proposal?.overridden;
  const scaleSignals = _topHitSignals(proposal?.business_scale_signals, 3);
  const modeKeywords = _formatKeywords(proposal?.detection_mode_signals, 5);

  const [overrideScale, setOverrideScale] = useState(businessScale);
  const [overrideMode, setOverrideMode] = useState(detectionMode);

  const isOverrideDirty =
    overrideScale !== businessScale || overrideMode !== detectionMode;

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    await onRegenerate({
      override_business_scale: overrideScale,
      override_detection_mode: overrideMode,
    });
    // After regenerate, the parent will pass in fresh proposal -> our pill
    // re-syncs via the next render.
  };

  return (
    <div
      className="rounded-sm border border-amber-200 bg-amber-50/40 p-3"
      data-testid="detection-badge"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-amber-700 flex-shrink-0" />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">
          Detected
        </span>
        <Badge
          variant="outline"
          className="text-[11px] uppercase tracking-wide font-semibold border-amber-300 bg-white text-amber-900 rounded-sm"
          data-testid="detection-badge-scale"
        >
          {businessScale}
        </Badge>
        <span className="text-amber-700/60 text-xs">·</span>
        <Badge
          variant="outline"
          className="text-[11px] uppercase tracking-wide font-semibold border-amber-300 bg-white text-amber-900 rounded-sm"
          data-testid="detection-badge-mode"
        >
          {detectionMode}
        </Badge>

        {overridden && (
          <Badge
            variant="outline"
            className="text-[10px] uppercase tracking-wider font-semibold border-stone-300 bg-stone-50 text-stone-700 rounded-sm"
            title={`Auto-detected: ${detectedScale} · ${detectedMode}`}
            data-testid="detection-badge-overridden"
          >
            Overridden
          </Badge>
        )}

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 hover:text-amber-900"
              data-testid="detection-signals-popover-trigger"
              aria-label="Why was this detected"
            >
              <Info className="h-3.5 w-3.5" />
              Why?
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-72 p-3 text-xs"
            data-testid="detection-signals-popover"
          >
            <p className="font-semibold text-stone-800 mb-2">
              How we read this opportunity
            </p>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mb-1">
              Business scale signals
            </p>
            {scaleSignals.length === 0 ? (
              <p className="text-stone-500 italic mb-2">No strong scale signals detected.</p>
            ) : (
              <ul className="space-y-1 mb-3">
                {scaleSignals.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-start gap-2 text-stone-700"
                    data-testid={`detection-signal-${s.id}`}
                  >
                    <span className="text-emerald-600 font-semibold">✓</span>
                    <span>{s.label}</span>
                  </li>
                ))}
              </ul>
            )}

            <p className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mb-1">
              Mode keywords matched
            </p>
            {modeKeywords.length === 0 ? (
              <p className="text-stone-500 italic">
                No event/seasonal keywords found — defaulted to standard B2B.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {modeKeywords.map((k) => (
                  <span
                    key={k}
                    className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 font-mono"
                  >
                    {k}
                  </span>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {!readOnly && (
        <>
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 hover:text-amber-900"
            onClick={() => setShowOverride((s) => !s)}
            data-testid="detection-override-toggle"
          >
            {showOverride ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            {showOverride ? 'Hide override' : 'Adjust detection'}
          </button>

          {showOverride && (
            <div
              className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end"
              data-testid="detection-override-controls"
            >
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-stone-600 mb-0.5 block">
                  Scale
                </label>
                <Select value={overrideScale} onValueChange={setOverrideScale}>
                  <SelectTrigger
                    className="h-8 text-xs"
                    data-testid="detection-override-scale-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCALE_OPTIONS.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        data-testid={`detection-override-scale-${o.value}`}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-stone-600 mb-0.5 block">
                  Mode
                </label>
                <Select value={overrideMode} onValueChange={setOverrideMode}>
                  <SelectTrigger
                    className="h-8 text-xs"
                    data-testid="detection-override-mode-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODE_OPTIONS.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        data-testid={`detection-override-mode-${o.value}`}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                onClick={handleRegenerate}
                disabled={!isOverrideDirty || regenerating || !onRegenerate}
                className="bg-amber-700 hover:bg-amber-800 text-white disabled:bg-amber-300 h-8"
                data-testid="detection-regenerate-btn"
                title={
                  !onRegenerate
                    ? 'Regenerate handler not provided'
                    : !isOverrideDirty
                    ? 'No change to regenerate'
                    : 'Regenerate proposal with these overrides'
                }
              >
                {regenerating ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                Regenerate
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
